import { makeAutoObservable } from 'mobx';
import { ServiceModel, Service } from '../../../model/professional/service-model';
import toastStore from '../../../store/toast-store';
import serviceStore from '../../../store/service-store';
import categoryStore from '../../../store/category-store';
import uiStore from '../../../store/ui-store';
import { ModalMode } from '../../../store/ui-store';
import { OptionCategory } from '../../../model/professional/option-category-model';
import { optionCategoryViewModel } from './option-category-view-model';
import { optionViewModel } from './option-view-model';
import companyStore from "../../../store/company-store";
import subscriptionStore from '../../../store/subscription-store';

class ServiceViewModel {
  isLoading: boolean = false;
  error: string | null = null;
  private serviceModel: ServiceModel;

  /**
   * Gère les états de chargement de manière centralisée
   * @param isLoading État de chargement à définir
   * @param resetError Si vrai, réinitialise également les erreurs
   */
  private setLoadingStates(isLoading: boolean, resetError: boolean = true): void {
    this.setLoading(isLoading);
    if (resetError && !isLoading) {
      this.setError(null);
    }
  }
  
  /**
   * Gère les erreurs de manière centralisée
   * @param message Message d'erreur à afficher
   * @param error Objet d'erreur optionnel pour le logging
   */
  private handleError(message: string, error?: any): void {
    console.error(message, error);
    this.setError(message);
    toastStore.error(message);
  }

  setLoading(value: boolean): void {
    this.isLoading = value;
  }

  setError(value: string | null): void {
    this.error = value;
  }
  
  constructor() {
    makeAutoObservable(this);
    this.serviceModel = ServiceModel.getInstance();
  }

  
  formatPrice(price: number): string {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  }
  
  formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
    }
    return `${mins} min`;
  }


  filterServices(searchTerm: string, statusFilter: 'all' | 'active' | 'inactive') {
    uiStore.setSearchTerm(searchTerm);
    uiStore.setStatusFilter(statusFilter);
  }
  
  // Pas besoin de stocker ces valeurs localement car elles sont dans uiStore
  
  // Getters pour accéder aux propriétés du uiStore
  get searchTerm(): string {
    return uiStore.searchTerm;
  }
  
  get statusFilter(): 'all' | 'active' | 'inactive' {
    return uiStore.statusFilter;
  }

  // Propriété calculée pour obtenir les services filtrés
  get filteredServices(): Service[] {
    const services = serviceStore.allServices;
    const searchTerm = uiStore.searchTerm.toLowerCase();
    const statusFilter = uiStore.statusFilter;

    // S'assurer que tous les services ont les propriétés nécessaires
    const validServices = services.filter(service => service !== undefined && service !== null).map(service => ({
      ...service,
      isActive: service.isActive !== undefined ? service.isActive : true,
      optionCategories: service.optionCategories || []
    }));

    return validServices.filter(service => {
      const matchesSearch = searchTerm === '' ||
        (service.name && service.name.toLowerCase().includes(searchTerm)) ||
        (service.description ? service.description.toLowerCase().includes(searchTerm) : false);

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && service.isActive) ||
        (statusFilter === 'inactive' && !service.isActive);

      return matchesSearch && matchesStatus;
    });
  }

  // Propriété calculée pour détecter les services sans délai d'annulation configuré
  get servicesWithoutCancellationDeadline(): Service[] {
    if (!subscriptionStore.hasFeature('depositPayment')) {
      return [];
    }

    return serviceStore.allServices.filter(service =>
      service.requiresBooking &&
      (service.cancellationRefundDeadlineHours === null || service.cancellationRefundDeadlineHours === undefined)
    );
  }

  get shouldShowCancellationDeadlineAlert(): boolean {
    return this.servicesWithoutCancellationDeadline.length > 0;
  }

  async getServices(): Promise<Service[]> {
    this.setLoadingStates(true);
    
    try {

      if(!companyStore.getIdCompanySelectedDefault) return []
      const response = await this.serviceModel.getServices(companyStore.getIdCompanySelectedDefault);
      
      if (!response || !response.success || !Array.isArray(response.data)) {
        const errorMsg = response?.message || 'Format de réponse invalide';
        this.handleError(errorMsg);
        return [];
      }
      
      // Charger les catégories et options pour chaque service
      const servicesWithDetails = await this.enrichServicesWithDetails(response.data);
      
      // Mettre à jour le store avec les données enrichies
      serviceStore.setServices(servicesWithDetails);
      
      return response.data;
    } catch (error: any) {
      const errorMessage = 'Impossible de récupérer les services';
      this.handleError(errorMessage, error);
      return [];
    } finally {
      this.setLoadingStates(false);
    }
  }
  
  /**
   * Enrichit les services avec les détails nécessaires pour l'affichage
   * 
   * Cette méthode prépare les données des services pour l'affichage dans l'interface utilisateur
   * en s'assurant que toutes les propriétés requises sont présentes et correctement formatées.
   * Elle fait partie du processus de normalisation des données entre le backend et le frontend.
   * 
   * Fonctionnement :
   * 1. Validation des données d'entrée pour éviter les erreurs
   * 2. Filtrage des services invalides (null, undefined)
   * 3. Normalisation des propriétés (isActive, optionCategories)
   * 4. Préparation d'un objet enrichi pour chaque service
   * 
   * @param services Tableau de services provenant de l'API
   * @returns Tableau de services enrichis et normalisés
   */
  private async enrichServicesWithDetails(services: Service[]): Promise<any[]> {
    // Validation des données d'entrée
    if (!services || !Array.isArray(services)) {
      console.error('enrichServicesWithDetails: services n\'est pas un tableau valide', services);
      return [];
    }
    
    return services
      // Filtrer les services invalides
      .filter(service => service !== undefined && service !== null)
      .map(service => {
        // Vérification de l'intégrité des données
        if (!service.id) {
          console.error('enrichServicesWithDetails: service sans ID valide', service);
          return null;
        }
        
        // Normalisation des propriétés
        const isActive = service.isActive !== undefined ? service.isActive : true;
        
        // Construction de l'objet enrichi
        return {
          ...service,
          isActive,
          optionCategories: (service as any).optionCategories || []
        };
      })
      // Filtrage final des objets invalides
      .filter(Boolean);
  }

  async getServiceById(serviceId: string): Promise<Service> {
    this.setLoadingStates(true);
    
    try {
      // Vérifier d'abord dans le store
      const serviceInStore = serviceStore.getServiceById(serviceId);
      if (serviceInStore) {
        this.setLoadingStates(false, false);
        return serviceInStore;
      }
      
      // Si pas dans le store, récupérer depuis l'API
      const response = await this.serviceModel.getServiceById(serviceId);
      
      if (!response.success || !response.data) {
        const errorMsg = response.message || `Service ${serviceId} non trouvé`;
        this.handleError(errorMsg);
        throw new Error(errorMsg);
      }
      
      return response.data;
    } catch (error: any) {
      const errorMessage = `Impossible de récupérer le service ${serviceId}`;
      this.handleError(errorMessage, error);
      throw error;
    } finally {
      this.setLoadingStates(false);
    }
  }



  /**
   * Active ou désactive un service
   * 
   * Cette méthode permet aux prestataires de contrôler la visibilité et la disponibilité
   * de leurs services sans les supprimer complètement. Un service désactivé n'est pas
   * visible pour les clients mais reste accessible pour le prestataire.
   * 
   * Flux de données :
   * 1. Mise à jour du statut via l'API
   * 2. Mise à jour immédiate du statut dans le store (optimistic update)
   * 3. Mise à jour complète du service avec les données de la réponse
   * 
   * @param serviceId ID du service à activer/désactiver
   * @param isActive Nouveau statut du service (true = actif, false = inactif)
   * @returns Le service mis à jour
   */
  async toggleServiceStatus(serviceId: string, isActive: boolean): Promise<Service> {
    this.setLoadingStates(true);
    
    try {
      const response = await this.serviceModel.toggleServiceStatus(serviceId, isActive);
      
      if (!response.success) {
        const errorMsg = response.message || `Impossible de ${isActive ? 'activer' : 'désactiver'} le service`;
        this.handleError(errorMsg);
        throw new Error(errorMsg);
      }
      
      // Mise à jour optimiste du statut dans le store
      serviceStore.updateServiceStatus(serviceId, isActive);
      
      if (response.data) {
        // Après réactivation, charger les détails complets du service pour avoir les catégories d'options
        if (isActive) {
          try {
            // Récupérer les détails complets du service, y compris les catégories d'options
            const serviceDetails = await this.getServiceById(serviceId);
            
            // S'assurer que le service a une propriété optionCategories valide
            const serviceWithDetails = {
              ...serviceDetails,
              optionCategories: Array.isArray(serviceDetails.optionCategories) 
                ? serviceDetails.optionCategories.map(cat => ({
                    ...cat,
                    options: Array.isArray(cat.options) ? cat.options : []
                  }))
                : []
            };
            
            serviceStore.updateServiceInStore(serviceWithDetails);
          } catch (error) {
            console.error('Erreur lors du chargement des détails du service:', error);
            // En cas d'erreur, mettre à jour avec les données de base de la réponse
            const fallbackService = {
              ...response.data,
              optionCategories: []
            };
            serviceStore.updateServiceInStore(fallbackService);
          }
        } else {
          // Pour la désactivation, on peut simplement mettre à jour avec les données de la réponse
          const serviceWithDetails = {
            ...response.data,
            optionCategories: Array.isArray(response.data.optionCategories) 
              ? response.data.optionCategories.map(cat => ({
                  ...cat,
                  options: Array.isArray(cat.options) ? cat.options : []
                }))
              : []
          };
          serviceStore.updateServiceInStore(serviceWithDetails);
        }
      }
      
      toastStore.success(response.message);
      
      return response.data || {} as Service;
    } catch (error: any) {
      const actionType = isActive ? 'activer' : 'désactiver';
      const errorMessage = `Impossible de ${actionType} le service ${serviceId}`;
      this.handleError(errorMessage, error);
      throw error;
    } finally {
      this.setLoadingStates(false);
    }
  }

  async deleteService(serviceId: string): Promise<void> {
    this.setLoadingStates(true);
    
    try {
      const response = await this.serviceModel.deleteService(serviceId);
      
      if (!response.success) {
        this.handleError(response.message);
        throw new Error(response.message || 'Échec de la suppression du service');
      }
      
      serviceStore.removeService(serviceId);
      
      toastStore.success(response.message);
    } catch (error: any) {
      const errorMessage = `Impossible de supprimer le service ${serviceId}`;
      this.handleError(errorMessage, error);
      throw error;
    } finally {
      this.setLoadingStates(false);
    }
  }

  // Getters pour les propriétés du modal
  get isServiceModalOpen(): boolean {
    return uiStore.serviceModal.isOpen;
  }
  
  get serviceModalMode(): ModalMode {
    return uiStore.serviceModal.mode;
  }
  
  get selectedService(): Service | null {
    return uiStore.serviceModal.selected;
  }
  
  // Méthodes pour la gestion du modal
  openCreateModal(): void {
    uiStore.openServiceModal('create');
  }
  
  async editService(service: Service): Promise<void> {
    uiStore.openServiceModal('edit', service);
    
    try {
      // Récupérer les catégories du service
      const categories = await optionCategoryViewModel.getCategories(service.id);
      
      // Récupérer toutes les options pour toutes les catégories en une seule fois
      await optionViewModel.getOptionsByServiceId(service.id);
      
      // Mettre à jour les catégories dans le store
      this.getCategoriesByServiceId(service.id);
    } catch (error) {
      console.error('Erreur lors du chargement des détails du service pour édition:', error);
      toastStore.error('Impossible de charger les détails du service pour édition');
    }
  }

  async viewService(service: Service): Promise<void> {
    uiStore.openServiceModal('view', service);
    
    try {
      // Récupérer les catégories du service
      const categories = await optionCategoryViewModel.getCategories(service.id);
      
      // Récupérer toutes les options pour toutes les catégories en une seule fois
      await optionViewModel.getOptionsByServiceId(service.id);
      
      // Mettre à jour les catégories dans le store
      this.getCategoriesByServiceId(service.id);
    } catch (error) {
      console.error('Erreur lors du chargement des détails du service:', error);
      toastStore.error('Impossible de charger les détails du service');
    }
  }
  
  closeServiceModal(): void {
    uiStore.closeServiceModal();
  }
  
  /**
   * Récupère un service depuis le store par son ID
   * Cette méthode est utilisée par les composants de vue pour accéder aux données
   * sans avoir à interagir directement avec le store
   * 
   * @param serviceId ID du service à récupérer
   * @returns Le service correspondant ou undefined si non trouvé
   */
  getServiceFromStore(serviceId: string): Service | undefined {
    return serviceStore.getServiceById(serviceId);
  }
  
  /**
   * Récupère les catégories associées à un service par son ID
   * Cette méthode est utilisée par les composants de vue pour accéder aux données
   * sans avoir à interagir directement avec le store
   * 
   * @param serviceId ID du service pour lequel récupérer les catégories
   * @returns Un tableau de catégories associées au service
   */
  getCategoriesByServiceId(serviceId: string): OptionCategory[] {
    const categories = categoryStore.getCategoriesByServiceId(serviceId);
    return categories;
  }
}

const serviceViewModel = new ServiceViewModel();

export const useServiceViewModel = () => {
  return serviceViewModel;
};

// Export pour les cas non-React (si nécessaire)
export default serviceViewModel;

